/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import {
  Mail,
  Trash2,
  Upload,
  ChevronDown,
  X,
  Check,
  Loader,
} from 'lucide-react';
import { debounce } from 'lodash';
import { posthog } from 'posthog-js';
import { toast } from 'sonner';
import { RootState } from '../../../reducers';
import { updateUserProfile, updateSettings } from '../settingsSlice';
import { updateTeamSettingsThunk } from '../../team/teamSlice';
import { useOutputLanguage } from '../../../hooks/useOutputLanguage';
import logger from '../../../lib/logger';
import { memoryService } from '../../../services/memoryService';
import ImageCropTool from './ImageCropTool';
import { MemoryBlockEditor } from './MemoryBlockEditor';
import builtInTemplates from '../../selectedVideo/tabs/templates/templates';
import { setAlertMessage } from '../../dashboard/dashboardSlice';
import { getSignoutUrl } from '../../../lib/routingUtils';
import {
  scheduleDeleteAccount,
  deletionPreflight,
} from '../../auth/authThunks';
import { getInitialState } from '../../app/appSlice';
import { setDeleteAccountDialogOpen } from '../../auth/authSlice';
import { useUserTags } from '../../../hooks/useTags';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import LanguageSelect from '../../library/files/FilesEmpty/LanguageSelect';
import OutputLanguageSelect from '../../../components/OutputLanguageSelect';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '../../../components/ui/avatar';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '../../../components/ui/alert';
import { Separator } from '../../../components/ui/separator';
import {
  SettingsLayout,
  SettingsSection,
} from '../../../components/ui/settings-section';
import { cn } from '../../../lib/utils';
import PasskeyManager from './PasskeyManager';

const AccountSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, status, recorder, team } = useSelector((state: RootState) => ({
    user: state?.settings.user,
    status: state?.settings.updateSettings.status,
    recorder: state?.settings.recorder,
    team: state?.team,
  }));
  const { name, email, picture } = user || {};
  const { teams } = useSelector((state: RootState) => state?.team);
  const { isDialogOpen: isDeleteAccountDialogOpen } = useSelector(
    (state: RootState) => state?.auth.deleteAccount,
  );
  const [newName, setNewName] = useState<string>(name);
  const [isPictureDialogOpen, setPictureDialogOpen] = useState(false);
  const [croppingProfilePicture, setCroppingProfilePicture] = useState<
    string | null
  >(null);
  const [newProfilePicture, setNewProfilePicture] = useState<string | null>(
    null,
  );
  const [isRemovingPicture, setIsRemovingPicture] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeSubscriptions, setActiveSubscriptions] = useState<any[]>([]);
  const [isCheckingSubscriptions, setIsCheckingSubscriptions] = useState(false);
  const [previewPicture, setPreviewPicture] = useState<
    string | null | undefined
  >(picture);
  const prevPictureRef = useRef<string | null | undefined>(picture);
  const prevStatusRef = useRef(status);
  const [forceUpdate, setForceUpdate] = useState(0);
  const emailAlerts = recorder?.otherSettings?.emailAlerts || false;
  const [transcriptionEmailAlerts, setTranscriptionEmailAlerts] =
    useState(emailAlerts);

  // Custom vocabulary state
  const { selectedTeam } = team;
  const customVocabulary = selectedTeam?.fileOptions?.customVocabulary || '';
  const [vocabularyChips, setVocabularyChips] = useState<string[]>(() => {
    return customVocabulary
      ? customVocabulary.split('\n').filter((term) => term.trim())
      : [];
  });
  const [newTerm, setNewTerm] = useState('');
  const [vocabError, setVocabError] = useState('');
  const [isVocabDialogOpen, setIsVocabDialogOpen] = useState(false);
  const { error: outputLanguageError } = useOutputLanguage();
  const { getTag, setTag } = useUserTags();

  // Get saved experience preference from user tags
  const savedExperience = getTag('media-actions-experience') as
    | 'classic'
    | 'new'
    | null;
  const useNewExperience = savedExperience === 'new';

  // Get files on sidebar preference from user tags
  const filesOnSidebarTag = getTag('files-on-sidebar');
  const filesOnSidebar = filesOnSidebarTag === 'true';

  // User Memory state
  const [memoryText, setMemoryText] = useState('');
  const [isMemoryDialogOpen, setIsMemoryDialogOpen] = useState(false);
  const [isLoadingMemory, setIsLoadingMemory] = useState(false);
  const [memoryError, setMemoryError] = useState('');

  // AI Template Notification state
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const [highlightedTemplateIndex, setHighlightedTemplateIndex] = useState(0);
  const templateInputRef = useRef<HTMLInputElement>(null);
  const templateDropdownRef = useRef<HTMLDivElement>(null);

  // Get saved default AI template from user tags
  const savedDefaultAiTemplate = getTag('default-ai-template') as string | null;

  // Update email alerts state when it changes
  useEffect(() => {
    setTranscriptionEmailAlerts(emailAlerts);
  }, [emailAlerts]);

  // Update vocabulary chips when customVocabulary changes
  useEffect(() => {
    const chips = customVocabulary
      ? customVocabulary.split('\n').filter((term) => term.trim())
      : [];
    setVocabularyChips(chips);
  }, [customVocabulary]);

  const handleEmailAlertsChange = (checked: boolean) => {
    setTranscriptionEmailAlerts(checked);
    dispatch(
      updateSettings({
        key: 'recorder',
        newSettings: {
          ...recorder,
          otherSettings: {
            ...recorder.otherSettings,
            emailAlerts: checked,
          },
        },
      }),
    );
  };

  // Custom vocabulary handlers
  const handleExperienceToggle = useCallback(() => {
    const newExperience = !useNewExperience;
    setTag({
      key: 'media-actions-experience',
      value: newExperience ? 'new' : 'classic',
    });
    toast(
      newExperience
        ? t('settings.betaRecorderEnabled', 'Beta recorder enabled')
        : t('settings.classicRecorderEnabled', 'Classic recorder enabled'),
      { duration: 2000 },
    );

    // Track Beta Recorder toggle in PostHog
    posthog.capture('beta-recorder-toggle', {
      enabled: newExperience,
      timestamp: new Date().toISOString(),
    });
  }, [useNewExperience, setTag, toast, t]);

  const handleFilesOnSidebarToggle = useCallback(() => {
    const newValue = !filesOnSidebar;
    setTag({
      key: 'files-on-sidebar',
      value: newValue ? 'true' : 'false',
    });
    toast(
      newValue
        ? t('settings.filesOnSidebarEnabled', 'Files on sidebar enabled')
        : t('settings.filesOnSidebarDisabled', 'Files on sidebar disabled'),
      { duration: 2000 },
    );
  }, [filesOnSidebar, setTag, toast, t]);

  const debouncedVocabUpdate = useCallback(
    debounce(async (chips: string[]) => {
      const newValue = chips.join('\n');
      try {
        await dispatch(
          updateTeamSettingsThunk({
            teamId: selectedTeam?.teamId || '',
            settings: {
              fileOptions: {
                customVocabulary: newValue,
              },
            },
          }),
        );
      } catch (err) {
        logger.error('Error updating custom vocabulary:', err);
        setVocabError(
          t(
            'settings.vocabulary.updateFailed',
            'Failed to update custom vocabulary',
          ),
        );
      }
    }, 1000),
    [selectedTeam?.teamId],
  );

  const handleAddTerm = (e?: React.FormEvent) => {
    e?.preventDefault();
    const term = newTerm.trim();
    if (term && !vocabularyChips.includes(term)) {
      const updatedChips = [...vocabularyChips, term];
      setVocabularyChips(updatedChips);
      setNewTerm('');
      setVocabError('');
      debouncedVocabUpdate(updatedChips);
    }
  };

  const handleDeleteTerm = (termToDelete: string) => {
    const updatedChips = vocabularyChips.filter(
      (term) => term !== termToDelete,
    );
    setVocabularyChips(updatedChips);
    setVocabError('');
    debouncedVocabUpdate(updatedChips);
  };

  // User Memory handlers
  const loadUserMemory = useCallback(async () => {
    setIsLoadingMemory(true);
    setMemoryError('');
    try {
      const memory = await memoryService.getMemory();
      setMemoryText(memory.memoryText || '');
    } catch (error) {
      logger.error('Error loading user memory:', error);
      setMemoryError(t('settings.memory.loadFailed', 'Failed to load memory'));
    } finally {
      setIsLoadingMemory(false);
    }
  }, [t]);

  // Debounced autosave for memory
  const debouncedMemorySave = useCallback(
    debounce(async (value: string) => {
      try {
        await memoryService.updateMemory(value);
      } catch (error) {
        logger.error('Error saving user memory:', error);
        setMemoryError(
          t('settings.memory.saveFailed', 'Failed to save memory'),
        );
      }
    }, 1000),
    [t],
  );

  const handleMemoryChange = (value: string) => {
    setMemoryText(value);
    debouncedMemorySave(value);
  };

  // Load memory when dialog opens
  useEffect(() => {
    if (isMemoryDialogOpen) {
      loadUserMemory();
    }
  }, [isMemoryDialogOpen, loadUserMemory]);

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!templateSearchQuery) return builtInTemplates;
    return builtInTemplates.filter((template) =>
      template.title.toLowerCase().includes(templateSearchQuery.toLowerCase()),
    );
  }, [templateSearchQuery]);

  // Get selected template title
  const selectedTemplateTitle = useMemo(() => {
    if (!savedDefaultAiTemplate) return null;
    const template = builtInTemplates.find(
      (tmpl) => tmpl.id === savedDefaultAiTemplate,
    );
    return template?.title || savedDefaultAiTemplate;
  }, [savedDefaultAiTemplate]);

  // Handle template selection
  const handleTemplateSelect = useCallback(
    (templateId: string | null) => {
      setTag({
        key: 'default-ai-template',
        value: templateId || '',
      });
      setIsTemplateDropdownOpen(false);
      setTemplateSearchQuery('');
      toast(
        templateId
          ? t(
              'settings.aiTemplateNotification.updated',
              'AI template notification updated',
            )
          : t(
              'settings.aiTemplateNotification.cleared',
              'AI template notification cleared',
            ),
        { duration: 2000 },
      );
    },
    [setTag, t],
  );

  // Keyboard navigation for template dropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isTemplateDropdownOpen) return;

      const optionsCount = filteredTemplates.length + 1; // +1 for "None" option

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedTemplateIndex((prev) =>
            prev < optionsCount - 1 ? prev + 1 : prev,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedTemplateIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedTemplateIndex === 0) {
            handleTemplateSelect(null);
          } else {
            const selectedTemplate =
              filteredTemplates[highlightedTemplateIndex - 1];
            if (selectedTemplate) {
              handleTemplateSelect(selectedTemplate.id);
            }
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsTemplateDropdownOpen(false);
          setTemplateSearchQuery('');
          break;
        default:
          break;
      }
    };

    if (isTemplateDropdownOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
    return undefined;
  }, [
    isTemplateDropdownOpen,
    highlightedTemplateIndex,
    filteredTemplates,
    handleTemplateSelect,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        templateDropdownRef.current &&
        !templateDropdownRef.current.contains(e.target as Node)
      ) {
        setIsTemplateDropdownOpen(false);
        setTemplateSearchQuery('');
      }
    };

    if (isTemplateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isTemplateDropdownOpen]);

  // Update state when user data changes
  useEffect(() => {
    setNewName(name);

    // Only update preview picture if it's different from the previous value
    // This prevents unnecessary re-renders
    if (picture !== prevPictureRef.current) {
      setPreviewPicture(picture);
      prevPictureRef.current = picture;
    }
  }, [name, picture]);

  // Show toast for successful profile updates
  useEffect(() => {
    if (status === 'pending') {
      // Status is pending
    } else if (status === 'succeeded' && prevStatusRef.current === 'pending') {
      setIsRemovingPicture(false);
      setNewProfilePicture(null);

      // Show success toast only when transitioning from pending to succeeded
      toast(t('profile.updated', 'Profile updated successfully'), {
        duration: 2000,
      });

      // Force update the preview picture with the current picture from Redux
      setPreviewPicture(picture);

      // Force a re-render
      setForceUpdate((prev) => prev + 1);
    } else if (status === 'failed') {
      setPreviewPicture(picture);

      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('profile.updateFailed', 'Failed to update profile'),
        }),
      );
    } // Update the previous status ref
    prevStatusRef.current = status;
  }, [status, dispatch, picture, t, toast]);

  // Update preview picture when newProfilePicture or isRemovingPicture changes
  useEffect(() => {
    if (isRemovingPicture) {
      setPreviewPicture(null);
    } else if (newProfilePicture) {
      setPreviewPicture(newProfilePicture);
    } else {
      setPreviewPicture(picture);
    }
  }, [newProfilePicture, isRemovingPicture, picture]);

  // Add a useEffect to update the preview picture when the Redux state changes
  useEffect(() => {
    // Update the preview picture when the Redux state changes
    if (!isRemovingPicture && !newProfilePicture) {
      setPreviewPicture(picture);
    }
  }, [picture, isRemovingPicture, newProfilePicture]);

  // Auto-update name with debounce
  const debouncedNameUpdate = useCallback(
    debounce((value: string) => {
      if (value && value !== name) {
        dispatch(updateUserProfile({ name: value }));
      }
    }, 1000),
    [name],
  );

  // Auto-update profile picture
  const updateProfilePicture = useCallback(
    (pictureData: any) => {
      dispatch(updateUserProfile(pictureData));
    },
    [dispatch],
  );

  const nameOnChange = (value?: string) => {
    if (value !== undefined) {
      setNewName(value);
      // Auto-update after user stops typing
      debouncedNameUpdate(value);
    }
  };

  const setDeleteDialogOpen = (open: boolean) => {
    dispatch(setDeleteAccountDialogOpen(open));
    if (!open) {
      setConfirmationText('');
      setActiveSubscriptions([]);
    }
  };

  const handleDeleteButtonClick = async () => {
    // Preflight check - read-only, does NOT schedule deletion
    setIsCheckingSubscriptions(true);
    const result: any = await dispatch(deletionPreflight());
    setIsCheckingSubscriptions(false);

    if (result.type?.endsWith('/rejected')) {
      toast.error(
        t(
          'profile.deletionCheckFailed',
          'Failed to check account status. Please try again later.',
        ),
        { duration: 5000 },
      );
      return;
    }

    const preflightData = result.payload?.data;

    // If already scheduled, inform the user
    if (preflightData?.alreadyScheduled) {
      dispatch(
        setAlertMessage({
          type: 'info',
          message: t(
            'profile.deletionAlreadyScheduled',
            'Account deletion is already scheduled.',
          ),
        }),
      );
      return;
    }

    // Check for active subscriptions
    if (
      preflightData?.subscriptions &&
      preflightData.subscriptions.length > 0
    ) {
      setActiveSubscriptions(preflightData.subscriptions);
      setDeleteDialogOpen(true);
      return;
    }

    // All checks passed - open the confirmation dialog
    setActiveSubscriptions([]);
    setDeleteDialogOpen(true);
  };

  const handleDeleteAccount = async () => {
    if (confirmationText === 'DELETE') {
      setIsDeleting(true);
      const result: any = await dispatch(scheduleDeleteAccount());

      // Check if the deletion was rejected
      if (result.type?.endsWith('/rejected')) {
        const error = result.payload;

        toast.error(
          error?.message ||
            t(
              'profile.deletionFailed',
              'Failed to delete account. Please try again later.',
            ),
          { duration: 5000 },
        );

        setIsDeleting(false);
        return;
      }

      // Success case
      dispatch(
        setAlertMessage({
          type: 'success',
          message: t(
            'profile.deletionInitiated',
            'Account deletion initiated. You will be logged out in 5 seconds.',
          ),
        }),
      );

      setTimeout(() => {
        navigate(getSignoutUrl());
      }, 5000);

      setDeleteDialogOpen(false);
      setIsDeleting(false);
    }
  };

  const handleRemoveProfilePicture = useCallback(() => {
    setIsRemovingPicture(true);
    setNewProfilePicture(null);
    setPreviewPicture(null);
    // Auto-update immediately
    updateProfilePicture({ picture: '' });
  }, [updateProfilePicture]);

  const handleChangePicture = useCallback(() => {
    setPictureDialogOpen(true);
    setIsRemovingPicture(false);
  }, []);

  const liveTeams = teams.filter((tm) => !tm.deleteRequestedAt).length;

  // Get the first letter of the name for avatar fallback
  const nameInitial = name ? name.charAt(0).toUpperCase() : '';

  // Force a re-render when the picture changes
  const avatarKey = `avatar-${picture || 'none'}-${
    isRemovingPicture ? 'removing' : 'normal'
  }-${newProfilePicture ? 'new' : 'none'}-${forceUpdate}`;

  return (
    <SettingsLayout>
      {/* Profile Picture Section */}
      <SettingsSection label={t('profile.profilePicture', 'Profile Picture')}>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar
              key={avatarKey}
              className="w-10 h-10"
              data-testid="profile-avatar"
            >
              <AvatarImage
                src={previewPicture || undefined}
                alt={name || 'User'}
              />
              <AvatarFallback className="text-sm bg-primary text-primary-foreground">
                {nameInitial}
              </AvatarFallback>
            </Avatar>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onClick={handleChangePicture}
                className="cursor-pointer"
              >
                <Upload className="h-4 w-4 mr-2" />
                {t('profile.uploadNewPhoto', 'Upload new photo')}
              </DropdownMenuItem>
              {previewPicture && (
                <DropdownMenuItem
                  onClick={handleRemoveProfilePicture}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('profile.removePhoto', 'Remove photo')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SettingsSection>

      {/* Name Section */}
      <SettingsSection label={t('settings.name', 'Name')}>
        <Input
          id="profileName"
          value={newName}
          onChange={(event) => nameOnChange(event.target.value)}
          required
          className={cn(
            'max-w-[200px]',
            !newName && 'border-red-500 focus:border-red-500',
          )}
          placeholder={t('settings.enterYourName', 'Enter your name')}
        />
      </SettingsSection>

      {/* Email Section */}
      <SettingsSection label={t('settings.email', 'Email')}>
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{email}</span>
        </div>
      </SettingsSection>

      {/* Email Alerts Section */}
      <SettingsSection
        label={t('settings.emailAlerts', 'Email Alerts')}
        description={t(
          'settings.emailAlertsDescription',
          'Receive email notifications after transcription is completed',
        )}
      >
        <Switch
          checked={transcriptionEmailAlerts}
          onCheckedChange={handleEmailAlertsChange}
        />
      </SettingsSection>

      {/* AI Template Notification Section */}
      <SettingsSection
        label={t('settings.aiTemplateNotification', 'AI Template Notification')}
        description={t(
          'settings.aiTemplateNotificationDescription',
          'Choose the AI template to run automatically when transcription completes',
        )}
      >
        <div className="relative" ref={templateDropdownRef}>
          <Button
            variant="outline"
            onClick={() => {
              setIsTemplateDropdownOpen(!isTemplateDropdownOpen);
              setHighlightedTemplateIndex(0);
            }}
            className="min-w-[180px] justify-between"
          >
            <span className="truncate">
              {selectedTemplateTitle ||
                t('settings.aiTemplateNotification.none', 'None')}
            </span>
            <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
          </Button>

          {isTemplateDropdownOpen && (
            <div className="absolute z-50 w-[280px] mt-1 bg-popover border rounded-md shadow-md">
              {/* Search input */}
              <div className="p-2 border-b">
                <Input
                  ref={templateInputRef}
                  value={templateSearchQuery}
                  onChange={(e) => {
                    setTemplateSearchQuery(e.target.value);
                    setHighlightedTemplateIndex(0);
                  }}
                  placeholder={t(
                    'settings.aiTemplateNotification.search',
                    'Search templates...',
                  )}
                  className="h-8"
                  autoFocus
                />
              </div>

              {/* Options list */}
              <div className="max-h-[300px] overflow-auto p-1">
                {/* None option */}
                <button
                  type="button"
                  className={cn(
                    'relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left outline-none transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    highlightedTemplateIndex === 0 &&
                      'bg-accent text-accent-foreground',
                  )}
                  onClick={() => handleTemplateSelect(null)}
                  onMouseEnter={() => setHighlightedTemplateIndex(0)}
                >
                  <X className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1">
                    {t('settings.aiTemplateNotification.none', 'None')}
                  </span>
                  {!savedDefaultAiTemplate && (
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </button>

                {/* Template options */}
                {filteredTemplates.map((template, index) => (
                  <button
                    type="button"
                    key={template.id}
                    className={cn(
                      'relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left outline-none transition-colors',
                      'hover:bg-accent hover:text-accent-foreground',
                      highlightedTemplateIndex === index + 1 &&
                        'bg-accent text-accent-foreground',
                    )}
                    onClick={() => handleTemplateSelect(template.id)}
                    onMouseEnter={() => setHighlightedTemplateIndex(index + 1)}
                  >
                    <span className="flex-1 truncate">{template.title}</span>
                    {savedDefaultAiTemplate === template.id && (
                      <Check className="h-4 w-4 shrink-0 text-primary" />
                    )}
                  </button>
                ))}

                {filteredTemplates.length === 0 && (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    {t(
                      'settings.aiTemplateNotification.noResults',
                      'No templates found',
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Transcription Language Section */}
      <SettingsSection
        label={t('settings.transcriptionLanguage', 'Transcription Language')}
        description={t(
          'settings.transcriptionLanguageDescription',
          "Choose the language for AI transcription. Select 'Auto' for automatic detection",
        )}
      >
        <LanguageSelect noCollapse />
      </SettingsSection>

      {/* Output Language Section */}
      <SettingsSection
        label={t('settings.outputLanguage', 'Output Language')}
        description={t(
          'settings.outputLanguageDescription',
          'Language for dictation and template outputs',
        )}
      >
        <OutputLanguageSelect />
      </SettingsSection>

      {/* Beta Recorder Section */}
      <SettingsSection
        label={t('settings.betaRecorder', 'Beta Recorder')}
        description={t(
          'settings.betaRecorderDescription',
          'Live transcribing recorder',
        )}
      >
        <Switch
          checked={useNewExperience}
          onCheckedChange={handleExperienceToggle}
        />
      </SettingsSection>

      {/* Files on Sidebar Section */}
      <SettingsSection
        label={t('settings.filesOnSidebar', 'Files on Sidebar')}
        description={t(
          'settings.filesOnSidebarDescription',
          'Show files instead of folders in the sidebar',
        )}
      >
        <Switch
          checked={filesOnSidebar}
          onCheckedChange={handleFilesOnSidebarToggle}
        />
      </SettingsSection>

      {/* Custom Vocabulary Section */}
      <SettingsSection
        label={t('settings.customVocabulary', 'Custom Vocabulary')}
        description={t(
          'settings.customVocabularyDescription',
          'Add specialized terms to improve transcription accuracy',
        )}
      >
        <div className="space-y-3">
          <Button
            variant="minimal"
            onClick={() => setIsVocabDialogOpen(true)}
            className="text-black hover:text-black"
          >
            {vocabularyChips.length === 0
              ? t('settings.vocabulary.manage', 'Manage')
              : `${vocabularyChips.length} ${vocabularyChips.length === 1 ? 'term' : 'terms'}`}
            <ChevronDown className="h-4 w-4 ml-1" />
          </Button>

          <Dialog open={isVocabDialogOpen} onOpenChange={setIsVocabDialogOpen}>
            <DialogContent className="sm:max-w-[425px] gap-3">
              <DialogHeader>
                <DialogTitle className="text-base">
                  {t('settings.customVocabulary', 'Custom Vocabulary')}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3">
                {/* Add new term form */}
                <form onSubmit={handleAddTerm}>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newTerm}
                      onChange={(e) => setNewTerm(e.target.value)}
                      placeholder={t(
                        'settings.vocabulary.addTerm',
                        'Add a term...',
                      )}
                      className="flex-1 h-9"
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={
                        !newTerm.trim() ||
                        vocabularyChips.includes(newTerm.trim())
                      }
                      className="h-9"
                    >
                      {t('settings.vocabulary.add', 'Add')}
                    </Button>
                  </div>
                </form>

                {/* Terms list */}
                {vocabularyChips.length > 0 ? (
                  <div className="border rounded-md max-h-[280px] overflow-y-auto">
                    <div className="divide-y">
                      {vocabularyChips.map((term) => (
                        <div
                          key={term}
                          className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 group transition-colors"
                        >
                          <span className="text-sm">{term}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTerm(term)}
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm text-muted-foreground">
                    {t('settings.vocabulary.noTermsYet', 'No terms added yet')}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {(vocabError || outputLanguageError) && (
            <p className="text-sm text-destructive">
              {vocabError || outputLanguageError}
            </p>
          )}
        </div>
      </SettingsSection>

      {/* User Memory Section */}
      <SettingsSection
        label={t('settings.userMemory', 'User Memory')}
        description={t(
          'settings.userMemoryDescription',
          'Store personal context and preferences for AI assistance',
        )}
      >
        <Button
          variant="minimal"
          onClick={() => setIsMemoryDialogOpen(true)}
          className="text-black hover:text-black"
        >
          {t('settings.memory.manage', 'Manage')}
        </Button>

        <Dialog open={isMemoryDialogOpen} onOpenChange={setIsMemoryDialogOpen}>
          <DialogContent className="sm:max-w-[650px]">
            <DialogHeader>
              <DialogTitle>
                {t('settings.userMemory', 'User Memory')}
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {t(
                  'settings.memory.description',
                  'Add information about yourself that AI assistants should remember.',
                )}
              </p>
            </DialogHeader>

            {isLoadingMemory ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="py-4">
                <MemoryBlockEditor
                  value={memoryText}
                  onChange={handleMemoryChange}
                />
              </div>
            )}

            {memoryError && (
              <p className="text-sm text-destructive">{memoryError}</p>
            )}

            <DialogFooter>
              <Button
                onClick={() => setIsMemoryDialogOpen(false)}
                disabled={isLoadingMemory}
              >
                {t('common.done', 'Done')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SettingsSection>

      {/* Passkeys Section */}
      <PasskeyManager />

      {/* Logout Section */}
      <SettingsSection label={t('settings.session', 'Session')}>
        <Link to={getSignoutUrl()}>
          <Button
            variant="minimal"
            data-testid="signout-button"
            className="text-black hover:text-black"
          >
            {t('settings.logout', 'Logout')}
          </Button>
        </Link>
      </SettingsSection>

      {/* Delete Account Section */}
      <SettingsSection
        label={t('profile.deleteAccount', 'Delete Account')}
        description={t(
          'profile.deleteAccountDescription',
          'Permanently delete your account and all data',
        )}
        showSeparator={false}
      >
        <Button
          variant="ghost"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={handleDeleteButtonClick}
          disabled={isCheckingSubscriptions}
        >
          {isCheckingSubscriptions
            ? t('profile.checking', 'Checking...')
            : t('profile.delete', 'Delete')}
        </Button>
      </SettingsSection>

      {/* Active Subscriptions Warning Dialog */}
      <Dialog
        open={
          isDeleteAccountDialogOpen &&
          liveTeams <= 1 &&
          activeSubscriptions.length > 0
        }
        onOpenChange={() => setDeleteDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {t('profile.unableToDelete', 'Unable to Delete Account')}
            </DialogTitle>
          </DialogHeader>
          <Separator className="my-4" />
          <div className="space-y-4">
            <Alert variant="warning">
              <AlertTitle>
                {t('profile.actionRequired', 'Action Required')}
              </AlertTitle>
              <AlertDescription>
                {t(
                  'profile.cancelSubscriptionFirst',
                  'Please cancel your subscription before deleting your account. Your subscription is set to auto-renew.',
                )}
              </AlertDescription>
            </Alert>

            <p className="text-sm">
              {t(
                'profile.activeSubscriptionsMessage',
                'You have active subscriptions that will auto-renew:',
              )}
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm">
              {activeSubscriptions.map((sub: any, index: number) => (
                <li key={index}>
                  <strong>{sub.teamName}</strong> ({sub.provider} -{' '}
                  {sub.package})
                </li>
              ))}
            </ul>

            <p className="text-sm">
              {t(
                'profile.cancelInstructions',
                'Please visit your billing settings to cancel these subscriptions, then try again.',
              )}
            </p>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                navigate('/settings/billing');
              }}
            >
              {t('profile.goToBilling', 'Go to Billing')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={
          isDeleteAccountDialogOpen &&
          liveTeams <= 1 &&
          activeSubscriptions.length === 0
        }
        onOpenChange={() => setDeleteDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {t('profile.deleteAccount', 'Delete Account')}
            </DialogTitle>
          </DialogHeader>
          <Separator className="my-4" />
          <div className="space-y-4">
            <Alert variant="warning">
              <AlertDescription>
                {t(
                  'profile.actionCannotBeUndone',
                  'This action cannot be undone',
                )}
              </AlertDescription>
            </Alert>

            <div>
              <p className="mb-2">
                {t('profile.deleteAccountWillDo', 'This will:')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>
                  {t(
                    'profile.deleteRecordings',
                    'Delete all your recordings and transcripts',
                  )}
                </li>
                <li>
                  {t('profile.removeTeams', 'Remove all your team memberships')}
                </li>
                <li>
                  {t(
                    'profile.cancelSubscriptions',
                    'Cancel any active subscriptions',
                  )}
                </li>
                <li>
                  {t(
                    'profile.permanentDeletion',
                    'Your account and all associated data will be permanently deleted after 14 days',
                  )}
                </li>
              </ul>
            </div>

            <div>
              <label
                htmlFor="deleteConfirmation"
                className="block text-sm font-medium mb-1"
              >
                {t('profile.typeDeleteToConfirm', 'Type DELETE to confirm')}
              </label>
              <Input
                id="deleteConfirmation"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                className={cn(
                  confirmationText !== '' &&
                    confirmationText !== 'DELETE' &&
                    'border-red-500',
                )}
              />
              {confirmationText !== '' && confirmationText !== 'DELETE' && (
                <p className="text-sm text-red-500 mt-1">
                  {t(
                    'profile.pleaseTypeDeleteExactly',
                    'Please type DELETE exactly as shown',
                  )}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={confirmationText !== 'DELETE' || isDeleting}
            >
              {isDeleting
                ? t('profile.deleting', 'Deleting...')
                : t('profile.deleteAccount', 'Delete Account')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unable to Delete Account Dialog */}
      <Dialog
        open={isDeleteAccountDialogOpen && liveTeams > 1}
        onOpenChange={() => setDeleteDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {t('profile.unableToDelete', 'Unable to Delete Account')}
            </DialogTitle>
          </DialogHeader>
          <Separator className="my-4" />
          <div className="space-y-4">
            <Alert variant="warning">
              <AlertTitle>
                {t('profile.actionRequired', 'Action Required')}
              </AlertTitle>
              <AlertDescription>
                {t(
                  'profile.deleteTeamsFirst',
                  'You need to delete or transfer ownership of your teams first',
                )}
              </AlertDescription>
            </Alert>

            <p className="text-sm">
              You are the primary owner of{' '}
              {teams.reduce((prev, tm, index) => {
                const teamLink = (
                  <button
                    key={tm.teamId}
                    type="button"
                    onClick={() =>
                      window.open(
                        `${window.location.href.split('#')[0]}#/settings/team?teamId=${tm.teamId}`,
                        '_blank',
                      )
                    }
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {tm.name}
                  </button>
                );
                return index === 0 ? [teamLink] : [prev, ', ', teamLink];
              }, [])}
              .{' '}
              {t(
                'profile.deleteTeamsBeforeAccount',
                'Please delete all except one team before proceeding with account deletion.',
              )}
            </p>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {t('profile.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={async () => {
                setDeleteDialogOpen(false);
                await dispatch(getInitialState());
                setDeleteDialogOpen(true);
              }}
            >
              {t('profile.retry', 'Retry')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Picture Dialog */}
      <Dialog
        open={isPictureDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPictureDialogOpen(false);
            setCroppingProfilePicture(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('profile.editPhoto', 'Edit profile photo')}
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 flex flex-col items-center">
            <div className="w-full max-w-[500px] flex flex-col items-center gap-4">
              {!croppingProfilePicture && (
                <p className="text-sm text-gray-500">
                  {t(
                    'profile.chooseOrDragPhoto',
                    'Choose a new profile photo or drag and drop one here',
                  )}
                </p>
              )}

              <ImageCropTool
                onCrop={(image: string) => {
                  setCroppingProfilePicture(image);
                }}
              />

              {croppingProfilePicture && (
                <div className="w-full p-4 border rounded-lg text-center">
                  <p className="text-sm font-medium mb-4">
                    {t('profile.preview', 'Preview')}
                  </p>
                  <div className="flex justify-center gap-8 items-center">
                    <div>
                      <p className="text-xs text-gray-500 mb-2">
                        {t('profile.current', 'Current')}
                      </p>
                      <Avatar className="w-20 h-20">
                        <AvatarImage src={picture || undefined} />
                        <AvatarFallback className="text-2xl">
                          {nameInitial}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-2">
                        {t('profile.new', 'New')}
                      </p>
                      <Avatar className="w-20 h-20">
                        <AvatarImage src={croppingProfilePicture} />
                      </Avatar>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPictureDialogOpen(false);
                setCroppingProfilePicture(null);
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              disabled={!croppingProfilePicture}
              onClick={() => {
                setPictureDialogOpen(false);
                setNewProfilePicture(croppingProfilePicture);
                setIsRemovingPicture(false);

                // Auto-update profile picture
                if (croppingProfilePicture) {
                  const dataURLMatches = /data:(.*);base64,(.+)/.exec(
                    croppingProfilePicture,
                  );
                  if (dataURLMatches?.length === 3) {
                    updateProfilePicture({
                      pictureMime: dataURLMatches[1],
                      picture: dataURLMatches[2],
                    });
                    setPreviewPicture(croppingProfilePicture);
                  }
                }

                setCroppingProfilePicture(null);
              }}
            >
              {t('profile.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsLayout>
  );
};

export default AccountSettings;
